import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { useAuthFetch } from "@/app/hooks/useAuthFetch";
import { useAnalytics } from "@/app/hooks/useAnalytics";
import { useHasContextualOverlay } from "@/app/components/ContextualOverlay";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Textarea } from "@/shared/components/ui/textarea";

type SubmitState = "idle" | "sending" | "sent" | "error";

// Brief window between "Thanks, got it" and auto-closing the dialog so
// the user notices the acknowledgement instead of the modal vanishing
// the instant the request returns.
const THANK_YOU_MS = 1400;

export function FeedbackButton() {
  const authFetch = useAuthFetch();
  const { track } = useAnalytics();
  const { pathname } = useLocation();
  const hasContextualOverlay = useHasContextualOverlay();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [status, setStatus] = useState<SubmitState>("idle");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setStatus("idle");
      queueMicrotask(() => textareaRef.current?.focus());
    } else {
      setText("");
      setStatus("idle");
    }
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || status === "sending") return;

    setStatus("sending");
    try {
      const res = await authFetch("/api/feedback", {
        method: "POST",
        body: JSON.stringify({
          feedback_type: "message",
          response: {
            text: trimmed,
            route: pathname,
            user_agent: navigator.userAgent,
          },
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      track("app:feedback_send", { route: pathname, length: trimmed.length });
      setStatus("sent");
      setText("");
      setTimeout(() => setOpen(false), THANK_YOU_MS);
    } catch {
      setStatus("error");
    }
  }

  return (
    <>
      {!hasContextualOverlay && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-lg transition hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="Send feedback"
        >
          <MessageSquare className="h-4 w-4" strokeWidth={2} />
          <span>Feedback</span>
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          {status === "sent" ? (
            <div className="py-6 text-center">
              <DialogTitle className="text-base font-semibold">
                Got it — reading every one of these
              </DialogTitle>
              <DialogDescription className="mt-2">
                Thanks for taking the time.
              </DialogDescription>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <DialogHeader>
                <DialogTitle>Send feedback</DialogTitle>
                <DialogDescription>
                  A thought, a bug, a wish — anything. Goes straight to Silver.
                </DialogDescription>
              </DialogHeader>
              <Textarea
                ref={textareaRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="What's on your mind?"
                rows={5}
                maxLength={5000}
                disabled={status === "sending"}
              />
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-slate-500">
                  {status === "error"
                    ? "Couldn't send — try again?"
                    : `Sending from ${pathname}`}
                </span>
                <Button
                  type="submit"
                  disabled={!text.trim() || status === "sending"}
                >
                  {status === "sending" ? "Sending…" : "Send"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
